// src/components/gamification/XPSystem.js
import React, { useState, useEffect } from 'react';
import './XPSystem.css';

// XP Configuration
const XP_ACTIONS = {
  TICKET_PURCHASE: 50,      // Buy a ticket
  TICKET_CHECKIN: 100,      // Check in to event
  PERK_CLAIM: 25,           // Claim a perk
  FIRST_PURCHASE: 100,      // First ticket ever
  BATCH_PURCHASE: 20,       // Extra XP per ticket in batch (3+ tickets)
  EARLY_BIRD: 75,           // Purchase 7+ days before event
  REFERRAL: 150,            // Refer a friend (future)
};

// Level thresholds
const LEVELS = [
  { level: 1, xpRequired: 0, title: 'Newcomer', color: '#9ca3af' },
  { level: 2, xpRequired: 100, title: 'Fan', color: '#60a5fa' },
  { level: 3, xpRequired: 300, title: 'Regular', color: '#34d399' },
  { level: 4, xpRequired: 600, title: 'Enthusiast', color: '#fbbf24' },
  { level: 5, xpRequired: 1000, title: 'VIP', color: '#f97316' },
  { level: 6, xpRequired: 1500, title: 'Legend', color: '#a855f7' },
  { level: 7, xpRequired: 2500, title: 'Master', color: '#ec4899' },
  { level: 8, xpRequired: 4000, title: 'Champion', color: '#ef4444' },
  { level: 9, xpRequired: 6000, title: 'Elite', color: '#8b5cf6' },
  { level: 10, xpRequired: 10000, title: 'Hall of Fame', color: '#fbbf24' },
];

// Storage key for XP data
const XP_STORAGE_KEY = 'user_xp_';

// Get user's XP data from localStorage
function getXPData(account) {
  if (!account) return null;

  try {
    const storageKey = XP_STORAGE_KEY + account.toLowerCase();
    const data = localStorage.getItem(storageKey);

    if (!data) {
      // Initialize new user
      return {
        totalXP: 0,
        level: 1,
        actions: [],
        achievements: [],
        lastUpdated: Date.now()
      };
    }

    return JSON.parse(data);
  } catch (error) {
    console.error('[XPSystem] Error reading XP data:', error);
    return null;
  }
}

// Save XP data to localStorage
function saveXPData(account, xpData) {
  if (!account) return;

  try {
    const storageKey = XP_STORAGE_KEY + account.toLowerCase();
    const dataToSave = {
      ...xpData,
      lastUpdated: Date.now()
    };
    localStorage.setItem(storageKey, JSON.stringify(dataToSave));
  } catch (error) {
    console.error('[XPSystem] Error saving XP data:', error);
  }
}

// Calculate level from total XP
function calculateLevel(totalXP) {
  for (let i = LEVELS.length - 1; i >= 0; i--) {
    if (totalXP >= LEVELS[i].xpRequired) {
      return LEVELS[i];
    }
  }
  return LEVELS[0];
}

// Sync XP based on owned tickets (for retroactive XP award)
export function syncXPFromTickets(account, ticketCount) {
  if (!account || ticketCount <= 0) return null;

  const xpData = getXPData(account);
  if (!xpData) return null;

  // Check if already synced
  const syncKey = `xp_synced_${account.toLowerCase()}`;
  const alreadySynced = localStorage.getItem(syncKey);
  if (alreadySynced) {
    console.log('[XPSystem] XP already synced for this account');
    return null;
  }

  // Calculate XP for existing tickets
  const xpPerTicket = XP_ACTIONS.TICKET_PURCHASE;
  const totalXPToAdd = ticketCount * xpPerTicket;
  const newTotalXP = xpData.totalXP + totalXPToAdd;
  const newLevel = calculateLevel(newTotalXP);

  // Add sync action to history
  const actionRecord = {
    action: 'RETROACTIVE_SYNC',
    xp: totalXPToAdd,
    timestamp: Date.now(),
    metadata: { ticketCount, reason: 'Synced from existing tickets' }
  };

  const updatedData = {
    ...xpData,
    totalXP: newTotalXP,
    level: newLevel.level,
    actions: [...xpData.actions, actionRecord]
  };

  saveXPData(account, updatedData);
  localStorage.setItem(syncKey, 'true');

  console.log(`[XPSystem] Synced ${totalXPToAdd} XP for ${ticketCount} existing tickets`);
  return { xpAwarded: totalXPToAdd, newTotalXP, ticketCount };
}

// Award XP function (exported for use in other components)
export function awardXP(account, action, metadata = {}) {
  if (!account || !XP_ACTIONS[action]) {
    console.warn('[XPSystem] Invalid XP award attempt:', { account, action });
    return null;
  }

  const xpData = getXPData(account);
  if (!xpData) return null;

  const xpAmount = XP_ACTIONS[action];
  const newTotalXP = xpData.totalXP + xpAmount;
  const oldLevel = calculateLevel(xpData.totalXP);
  const newLevel = calculateLevel(newTotalXP);

  // Add action to history
  const actionRecord = {
    action,
    xp: xpAmount,
    timestamp: Date.now(),
    metadata
  };

  const updatedData = {
    ...xpData,
    totalXP: newTotalXP,
    level: newLevel.level,
    actions: [...xpData.actions, actionRecord]
  };

  // Check for level up
  const leveledUp = newLevel.level > oldLevel.level;
  if (leveledUp) {
    updatedData.achievements = [
      ...xpData.achievements,
      {
        type: 'LEVEL_UP',
        level: newLevel.level,
        timestamp: Date.now()
      }
    ];
  }

  saveXPData(account, updatedData);

  return {
    xpAwarded: xpAmount,
    newTotalXP,
    oldLevel,
    newLevel,
    leveledUp
  };
}

export default function XPSystem({ account, showDetails = false }) {
  const [xpData, setXpData] = useState(null);
  const [currentLevel, setCurrentLevel] = useState(null);
  const [nextLevel, setNextLevel] = useState(null);
  const [progressPercent, setProgressPercent] = useState(0);

  useEffect(() => {
    if (!account) return;

    const data = getXPData(account);
    if (!data) return;

    setXpData(data);

    const level = calculateLevel(data.totalXP);
    setCurrentLevel(level);

    // Find next level
    const nextLevelIndex = LEVELS.findIndex(l => l.level === level.level + 1);
    if (nextLevelIndex !== -1) {
      const next = LEVELS[nextLevelIndex];
      setNextLevel(next);

      // Calculate progress percentage
      const currentLevelXP = level.xpRequired;
      const nextLevelXP = next.xpRequired;
      const xpIntoLevel = data.totalXP - currentLevelXP;
      const xpNeededForNext = nextLevelXP - currentLevelXP;
      const percent = (xpIntoLevel / xpNeededForNext) * 100;
      setProgressPercent(Math.min(100, Math.max(0, percent)));
    } else {
      // Max level reached
      setNextLevel(null);
      setProgressPercent(100);
    }
  }, [account]);

  if (!xpData || !currentLevel) {
    return null;
  }

  const xpNeeded = nextLevel ? nextLevel.xpRequired - xpData.totalXP : 0;

  return (
    <div className="xp-system">
      {/* Compact XP Display */}
      <div className="xp-compact">
        <div className="xp-level-badge" style={{ borderColor: currentLevel.color }}>
          <span className="xp-level-number">{currentLevel.level}</span>
          <span className="xp-level-title">{currentLevel.title}</span>
        </div>

        <div className="xp-progress-container">
          <div className="xp-progress-bar">
            <div
              className="xp-progress-fill"
              style={{
                width: `${progressPercent}%`,
                background: `linear-gradient(90deg, ${currentLevel.color}, ${nextLevel?.color || currentLevel.color})`
              }}
            />
          </div>
          <div className="xp-info">
            <span className="xp-current">{xpData.totalXP} XP</span>
            {nextLevel && (
              <span className="xp-next">
                {xpNeeded} XP to {nextLevel.title}
              </span>
            )}
            {!nextLevel && (
              <span className="xp-max">MAX LEVEL</span>
            )}
          </div>
        </div>
      </div>

      {/* Detailed XP View */}
      {showDetails && (
        <div className="xp-details">
          <h4>XP Breakdown</h4>

          {/* Recent Actions */}
          <div className="xp-recent-actions">
            <h5>Recent Activity</h5>
            {xpData.actions.length === 0 ? (
              <p className="xp-empty">No activity yet. Purchase tickets to earn XP!</p>
            ) : (
              <div className="xp-actions-list">
                {xpData.actions.slice(-5).reverse().map((action, index) => (
                  <div key={index} className="xp-action-item">
                    <span className="xp-action-name">{formatActionName(action.action)}</span>
                    <span className="xp-action-amount">+{action.xp} XP</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Level Progress */}
          <div className="xp-level-progress">
            <h5>Level Progress</h5>
            <div className="xp-levels-grid">
              {LEVELS.map((level) => (
                <div
                  key={level.level}
                  className={`xp-level-item ${
                    level.level === currentLevel.level ? 'current' : ''
                  } ${
                    level.level < currentLevel.level ? 'completed' : ''
                  }`}
                  style={{
                    borderColor: level.level <= currentLevel.level ? level.color : '#374151'
                  }}
                >
                  <div className="xp-level-num">{level.level}</div>
                  <div className="xp-level-name">{level.title}</div>
                  <div className="xp-level-req">{level.xpRequired} XP</div>
                </div>
              ))}
            </div>
          </div>

          {/* XP Actions Reference */}
          <div className="xp-actions-reference">
            <h5>How to Earn XP</h5>
            <div className="xp-reference-list">
              <div className="xp-ref-item">
                <span className="xp-ref-name">Purchase Ticket</span>
                <span className="xp-ref-amount">+{XP_ACTIONS.TICKET_PURCHASE} XP</span>
              </div>
              <div className="xp-ref-item">
                <span className="xp-ref-name">Check-in to Event</span>
                <span className="xp-ref-amount">+{XP_ACTIONS.TICKET_CHECKIN} XP</span>
              </div>
              <div className="xp-ref-item">
                <span className="xp-ref-name">Claim Perk</span>
                <span className="xp-ref-amount">+{XP_ACTIONS.PERK_CLAIM} XP</span>
              </div>
              <div className="xp-ref-item">
                <span className="xp-ref-name">First Purchase Bonus</span>
                <span className="xp-ref-amount">+{XP_ACTIONS.FIRST_PURCHASE} XP</span>
              </div>
              <div className="xp-ref-item">
                <span className="xp-ref-name">Batch Purchase Bonus</span>
                <span className="xp-ref-amount">+{XP_ACTIONS.BATCH_PURCHASE} XP/ticket</span>
              </div>
              <div className="xp-ref-item">
                <span className="xp-ref-name">Early Bird Bonus</span>
                <span className="xp-ref-amount">+{XP_ACTIONS.EARLY_BIRD} XP</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function to format action names
function formatActionName(action) {
  const names = {
    TICKET_PURCHASE: 'Ticket Purchased',
    TICKET_CHECKIN: 'Event Check-in',
    PERK_CLAIM: 'Perk Claimed',
    FIRST_PURCHASE: 'First Purchase',
    BATCH_PURCHASE: 'Batch Purchase',
    EARLY_BIRD: 'Early Bird',
    REFERRAL: 'Referral',
    RETROACTIVE_SYNC: 'Existing Tickets Sync'
  };
  return names[action] || action;
}
